/**
 * Static demo data for the Trial Plan of Record mockup. Shapes mirror what a real API
 * would return so a service layer can replace this without a rewrite.
 *
 * Design reasoning and the departures from the PRD live in docs/PRD_MOCKUPS_DESIGN_NOTES.md.
 */

/** P0-4 provenance classes, plus `carried-forward` for plans originating pre-live in Trial Planning. */
export type ProvenanceClass =
  | "user-entered"
  | "imported"
  | "vendor-reported"
  | "carried-forward";

export type MilestoneScope = "study" | "country" | "site";

/** Derived from the planned date, the actual date and the milestone's thresholds. */
export type MilestoneStatus = "on-track" | "at-risk" | "late" | "complete";

/**
 * Optional early warning on a milestone. There is no default, because a guessed threshold
 * makes "at risk" appear on milestones nobody has judged to be at risk.
 */
export interface StatusThresholds {
  /** Days before the planned date at which an un-actualled milestone reads at risk. */
  atRiskWithinDays: number;
}

/** P0-1 milestone type library (configurable in the real thing). */
export type MilestoneType =
  | "first-patient-in"
  | "last-patient-in"
  | "site-activation"
  | "database-lock"
  | "readout"
  | "custom";

export interface AuditEntry {
  id: string;
  // Nullable: the backend's created_by/last_modified_by FKs are SET_NULL, so a deleted
  // user leaves this null rather than absent.
  actor: string | null;
  actorRole?: string;
  at: string;
  action: "created" | "updated" | "deleted";
  field?: string;
  priorValue?: string | null;
  newValue?: string | null;
  rationale?: string;
}

export interface Milestone {
  id: string;
  type: MilestoneType;
  label: string;
  scope: MilestoneScope;
  scopeLabel: string;
  plannedDate: string;
  /** First planned date, immutable. Null if added after the plan was carried forward. */
  originalPlannedDate: string | null;
  /** When this milestone entered the plan of record, and who put it there. */
  createdAt: string;
  createdBy: string | null;
  actualDate: string | null;
  thresholds: StatusThresholds | null;
  provenance: ProvenanceClass;
  rationale: string | null;
  audit: AuditEntry[];
}

/** P0-1 — the configurable milestone type library offered when adding a milestone. */
export const MILESTONE_TYPES: Array<{
  value: MilestoneType;
  label: string;
  /** Suggested name, so the common cases need no typing. */
  suggestedLabel: string;
}> = [
  {
    value: "first-patient-in",
    label: "First patient in",
    suggestedLabel: "First patient in",
  },
  {
    value: "last-patient-in",
    label: "Last patient in",
    suggestedLabel: "Last patient in",
  },
  {
    value: "site-activation",
    label: "Site activation",
    suggestedLabel: "Site activation",
  },
  {
    value: "database-lock",
    label: "Database lock",
    suggestedLabel: "Database lock",
  },
  { value: "readout", label: "Readout", suggestedLabel: "Topline readout" },
  { value: "custom", label: "Custom", suggestedLabel: "" },
];

/** Countries and sites a milestone can be scoped to, for the scope picker. */
export const PLAN_COUNTRIES = [
  "Germany",
  "United States",
  "United Kingdom",
  "Spain",
] as const;

export const PLAN_SITES = [
  { siteNumber: "104", siteName: "Charité Berlin" },
  { siteNumber: "109", siteName: "Universitätsklinikum Köln" },
  { siteNumber: "118", siteName: "Massachusetts General Hospital" },
  { siteNumber: "122", siteName: "Johns Hopkins" },
  { siteNumber: "131", siteName: "Guy's and St Thomas'" },
  { siteNumber: "140", siteName: "Hospital Clínic Barcelona" },
] as const;

/** What a plan editor changes on an existing milestone. */
export interface MilestoneEdit {
  plannedDate: string;
  actualDate: string | null;
  thresholds: StatusThresholds | null;
}

/** Everything needed to create a milestone. */
export interface MilestoneDraft {
  type: MilestoneType;
  label: string;
  scope: MilestoneScope;
  scopeLabel: string;
  plannedDate: string;
  actualDate: string | null;
  thresholds: StatusThresholds | null;
  countryName?: string;
  siteNumber?: string;
}
